import type { SagaIterator } from 'redux-saga';
import { call, delay, put, select, takeEvery } from 'redux-saga/effects';

import { getLogger } from '../../utils/core';
import { accountsActions } from '../accounts';
import { chartOfAccountsTypes } from '../chart-of-accounts';
import { actionHelpers, resourceSagaUtils, resourceStoreTypes } from '../core';
import type { QcsSyncResourcesConfigProps, ResourceSagaAction } from '../core/resourceStoreTypes';
import { tagsActions } from '../tags';
import { transactionsActions } from '../transactions';
import * as actions from './scheduledTransactionsActions';
import * as scheduledTransactionsSelectors from './scheduledTransactionsSelectors';
import * as scheduledTransactionsTransformers from './scheduledTransactionsTransformers';
import type { ScheduledTransaction, ScheduledTransactionType } from './scheduledTransactionsTypes';
import * as scheduledTransactionsTypes from './scheduledTransactionsTypes';

const logger = getLogger('flux/scheduledTransactionsSagas.js');

export function* scheduledTransactionsDumpUpdater(resources: ScheduledTransactionType[]) {
  const scheduledTransactions = resources.map(scheduledTransactionsTransformers.transformDataToScheduledTransaction);
  yield put(
    actions.getScheduledTransactionsSuccess({ resources: scheduledTransactions }, { context: 'BUMP_CHANGE_DUMP' }),
  );
}

export function* scheduledTransactionsNudgeUpdater() {
  yield put(actions.getScheduledTransactions(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
}

const mkQcsSyncScheduledTransactionConfig =
  resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<ScheduledTransactionType>();

const mkResourceConfig = (props: Partial<QcsSyncResourcesConfigProps<ScheduledTransactionType>>) =>
  mkQcsSyncScheduledTransactionConfig({
    resourceName: 'scheduled transactions',
    resourceBaseUrl: '/scheduled-transactions',
    getLastSyncDate: scheduledTransactionsSelectors.getLastSyncDate,

    transformServerResourceToClientResource: scheduledTransactionsTransformers.transformDataToScheduledTransaction,
    transformClientResourceToRequestData: scheduledTransactionsTransformers.transformScheduledTransactionToRequestData,
    ...props,
  });

export function* getScheduledTransactionsActionWatcher() {
  const config = mkResourceConfig({
    successAction: actions.getScheduledTransactionsSuccess,
    failureAction: actions.getScheduledTransactionsFailure,
    axiosConfig: {
      params: {
        includeCascadeDeleted: true,
      },
    },
  });
  yield resourceSagaUtils.takeAndDebounce(
    actions.getScheduledTransactions,
    resourceSagaUtils.qcsSyncGetResources<ScheduledTransactionType>,
    config,
  );
}

export function* createScheduledTransactionActionWatcher() {
  const config = mkResourceConfig({
    successAction: actions.createScheduledTransactionSuccess,
    failureAction: actions.createScheduledTransactionFailure,
  });
  yield takeEvery(
    actions.createScheduledTransaction.type,
    resourceSagaUtils.qcsSyncCreateResource<scheduledTransactionsTypes.ScheduledTransactionType>,
    config,
  );
}

// Without an id the update would PUT the collection URL, which QCS treats as a
// bulk update and rejects with a 400 (SIMPL-30330). Fail it locally instead so
// the optimistic upsert is rolled back without a request.
export function* updateScheduledTransactionWithId(
  config: ReturnType<typeof mkResourceConfig>,
  action: ResourceSagaAction<ScheduledTransactionType>,
): SagaIterator {
  if (!action.payload?.id) {
    const error = new Error('Cannot update a scheduled transaction without an id');
    logger.error(error.message, { clientId: action.payload?.clientId });
    yield put(
      actions.updateScheduledTransactionFailure(
        Object.assign({ clientId: action.payload?.clientId }, error),
        actionHelpers.cpMetaForResponseAction(action.meta) ?? undefined,
      ),
    );
    action.meta?.reject?.(error);
    return undefined;
  }
  return yield call(
    resourceSagaUtils.qcsSyncUpdateResource<scheduledTransactionsTypes.ScheduledTransactionType>,
    config,
    action,
  );
}

export function* updateScheduledTransactionActionWatcher() {
  const config = mkResourceConfig({
    successAction: actions.updateScheduledTransactionSuccess,
    failureAction: actions.updateScheduledTransactionFailure,
  });
  yield takeEvery(actions.updateScheduledTransaction.type, updateScheduledTransactionWithId, config);
}

export function* deleteScheduledTransactionActionWatcher() {
  const config = mkResourceConfig({
    successAction: actions.deleteScheduledTransactionSuccess,
    failureAction: actions.deleteScheduledTransactionFailure,
  });
  yield takeEvery(
    actions.deleteScheduledTransaction.type,
    resourceSagaUtils.qcsSyncDeleteResource<scheduledTransactionsTypes.ScheduledTransactionType>,
    config,
  );
}

export function* triggerGetTransactionsAction() {
  yield delay(3000); // dirty hack: we have to wait for random number of seconds while QCS generates transactions asynchronously
  yield put(transactionsActions.getTransactions(undefined, { context: 'scheduledTransactions' }));
}

export function* createScheduledTransactionSuccessActionWatcher() {
  yield takeEvery(actions.createScheduledTransactionSuccess, triggerGetTransactionsAction);
}
export function* updateScheduledTransactionSuccessActionWatcher() {
  yield takeEvery(actions.updateScheduledTransactionSuccess, triggerGetTransactionsAction);
}
export function* deleteScheduledTransactionSuccessActionWatcher() {
  yield takeEvery(actions.deleteScheduledTransactionSuccess, triggerGetTransactionsAction);
}
export function* createRecurringTransactionSuccessActionWatcher() {
  yield takeEvery(actions.createBillScoutScheduleSuccess, triggerGetTransactionsAction);
}

export function* createRecurringTransactionWatcher() {
  const config = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<actions.CreateBillScoutSchedulePayload>()({
    resourceName: 'bill scout',
    resourceBaseUrl: '/bill-scout/create-recurring-transaction',
    transformResourceToRequestData: scheduledTransactionsTransformers.transformBillScoutScheduleToRequestData,
    successAction: actions.createBillScoutScheduleSuccess,
    failureAction: actions.createBillScoutScheduleFailure,
    getLastSyncDate: () => null,
  });
  yield takeEvery(
    actions.createBillScoutSchedule.type,
    resourceSagaUtils.qcsSyncUpdateResource<actions.CreateBillScoutSchedulePayload>,
    config,
  );
}

export function* batchScheduledTransactionsActionWatcher() {
  const config = mkResourceConfig({
    successAction: actions.batchScheduledTransactionsSuccess,
    failureAction: actions.batchScheduledTransactionsFailure,
  });
  yield takeEvery(
    actions.batchScheduledTransactions.type,
    resourceSagaUtils.qcsSyncUpdateResources<scheduledTransactionsTypes.ScheduledTransactionType>,
    config,
  );
}

export function* deleteTagActionWatcher() {
  yield takeEvery(tagsActions.deleteTag, function* deleteTag(action: any) {
    if (action?.meta?.schedTxns?.size > 0) {
      yield put(actions.batchScheduledTransactions(action.meta.schedTxns));
    }
  });
}

export function* deleteAccountActionWatcher() {
  yield takeEvery(accountsActions.deleteAccountSuccess, function* checkScheduledTransactions(action): SagaIterator {
    const accountId = action?.payload?.id;
    const scheduledTransactions = yield select(scheduledTransactionsSelectors.getScheduledTransactions);
    const modelsToUpdate: scheduledTransactionsTypes.ScheduledTransaction[] = [];
    scheduledTransactions.forEach((model: ScheduledTransaction) => {
      if (model.transaction?.accountId === accountId) {
        logger.warn('ScheduledTxn associated with deleted accountId - should we delete?');
      }
      if (model.transaction?.coa?.type === 'ACCOUNT' && model.transaction?.coa?.id === accountId) {
        logger.debug('Found transfer model to deleted account - setting to standard transfer');

        // set coa to base transfer
        let newModel = model.setIn(['transaction', 'coa'], chartOfAccountsTypes.l1TransferCoa);

        // adjust Exclude flags to not include connected-transfer options
        if (newModel.excludeF2S === scheduledTransactionsTypes.STExcludeEnum.DEST) {
          newModel = newModel.set('excludeF2S', scheduledTransactionsTypes.STExcludeEnum.NONE);
        } else if (newModel.excludeF2S === scheduledTransactionsTypes.STExcludeEnum.BOTH) {
          newModel = newModel.set('excludeF2S', scheduledTransactionsTypes.STExcludeEnum.SRC);
        }
        if (newModel.excludeReports === scheduledTransactionsTypes.STExcludeEnum.DEST) {
          newModel = newModel.set('excludeReports', scheduledTransactionsTypes.STExcludeEnum.NONE);
        } else if (newModel.excludeReports === scheduledTransactionsTypes.STExcludeEnum.BOTH) {
          newModel = newModel.set('excludeReports', scheduledTransactionsTypes.STExcludeEnum.SRC);
        }

        modelsToUpdate.push(newModel);
      }
    });

    if (modelsToUpdate?.length) {
      yield put(actions.batchScheduledTransactions(modelsToUpdate));
    }
  });
}

export default [
  createScheduledTransactionActionWatcher,
  createScheduledTransactionSuccessActionWatcher,
  updateScheduledTransactionActionWatcher,
  updateScheduledTransactionSuccessActionWatcher,
  deleteScheduledTransactionActionWatcher,
  deleteScheduledTransactionSuccessActionWatcher,
  getScheduledTransactionsActionWatcher,
  createRecurringTransactionSuccessActionWatcher,
  createRecurringTransactionWatcher,
  batchScheduledTransactionsActionWatcher,
  deleteTagActionWatcher,
  deleteAccountActionWatcher,
];
